// components/markers/SingleMarker.jsx

import { Pin, useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Map, StopCircle } from "react-feather";
import { imageSet } from "../../../pages/icons/AssetIcon/IconList";
import { getUrl } from "../../../utils/staticMethods";
import defaultImg from "../../../assets/img/icon/Aircraft/Airliner_Black.png";
import useSrlHelper from "../../../hooks/useSrlHelper";
import InfoWindow from "../../mapComponents/InfoWindow";
import useCommonMap from "../../../hooks/useCommonMap";
import { createPortal } from "react-dom";

/**
 * Displays a single item marker.
 */
export function SingleMarker({ marker, item, isSelected, showInfo, onSelect }) {
  const { itemDisplayName } = useSrlHelper();
  const displayName = useMemo(() => itemDisplayName(item));
  const { showInfoIds, setShowInfoIds } = useCommonMap();
  const showInfoWindow = showInfoIds.includes(item.id);
  const markerRef = useRef(null);
  const [markerPos, setMarkerPos] = useState({ top: 0, left: 0 });
  const map = useMap();
  const mapLibCore = useMapsLibrary("core");

  const handleBoundsChange = () => {
    measurePosition();
  };

  useEffect(() => {
    if (!map) return;
    if (!showInfoWindow) return;

    const boundsChangedListener = map.addListener(
      "bounds_changed",
      handleBoundsChange
    );

    const idleListener = map.addListener("idle", handleBoundsChange);

    return () => {
      mapLibCore.event.removeListener(boundsChangedListener);
      mapLibCore.event.removeListener(idleListener);
    };
  }, [map, showInfoWindow]);

  const imageSrc = useMemo(
    () =>
      imageSet[getUrl(item?.icon)]
        ? require("../../../assets/img/icon/" + imageSet[getUrl(item.icon)])
            .default
        : defaultImg,
    [item?.icon]
  );

  const clickTarget = useCallback(
    (item) => {
      onSelect();
    },
    [onSelect]
  );

  const onClose = useCallback(() => {
    setShowInfoIds((prev) => prev.filter((i) => i !== item.id));
  }, []);

  const measurePosition = useCallback(() => {
    if (!map) return;
    if (markerRef.current) {
      const markerRect = markerRef.current.getBoundingClientRect();
      const mapRect = map.getDiv().getBoundingClientRect();

      setMarkerPos({
        // Position the InfoWindow above the marker
        // top: markerRect.top - 8,
        // left: markerRect.left + markerRect.width / 2,
        // top: markerRect.top + mapRect.top - 8,
        // left: markerRect.left + mapRect.left + markerRect.width / 2,
        top: markerRect.top - mapRect.top - 8,
        left: markerRect.left - mapRect.left + markerRect.width / 2,
      });
    }
  }, [map]);

  // Measure once when InfoWindow opens
  useEffect(() => {
    if (showInfoWindow) {
      measurePosition();
    }
  }, [showInfoWindow, marker.position]);

  // useEffect(() => {
  //   measurePosition();
  // }, [measurePosition]);

  return (
    <>
      <div style={{ position: "relative", transform: "translateY(50%)" }}>
        <img
          ref={markerRef}
          src={imageSrc}
          width={24}
          alt="Marker"
          onClick={() => {
            setShowInfoIds((prev) =>
              prev.includes(item.id)
                ? prev.filter((i) => i !== item.id)
                : [...prev, item.id]
            );
            onSelect && onSelect();
          }}
          className="cursor-pointer"
        />
        <div
          className="px-1 text-nowrap text-black bg-primary bg-opacity-75 rounded"
          style={{
            position: "absolute",
            top: "100%",
            left: "50%",
            transform: "translateX(-50%)",
            marginTop: 4,
            pointerEvents: "none",
          }}
        >
          {displayName}
        </div>
      </div>

      {map &&
        showInfoWindow &&
        createPortal(
          <InfoWindow
            key={item.id}
            clickTarget={clickTarget}
            show={Object.assign({}, item, { active: isSelected })}
            onClose={onClose}
            anchorPos={markerPos} // 👈 pass measured position
          />,
          map.getDiv()
        )}
    </>
  );
}
